import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { t } from '../i18n';
import { useOfflineState } from '../offline';
import { dismissProblem, listProblems, type SyncProblem } from '../offline/outbox';
import { classNames } from '../util';

/** Verbindungsstatus unten links: nur sichtbar, wenn etwas zu sagen ist. */
export function SyncStatus() {
  const s = useOfflineState();
  const [open, setOpen] = useState(false);
  if (s.online && s.pending === 0 && s.problems === 0) return null;
  return (
    <>
      <div className="sync-status" role="status" aria-live="polite">
        {!s.online ? (
          <span className="sync-pill offline">
            <span className="sync-dot" /> {t('offline.offline')}
            {s.pending > 0 && <> · {t('offline.pending', { n: s.pending })}</>}
          </span>
        ) : (
          s.pending > 0 && <span className="sync-pill sending">↻ {t('offline.sending', { n: s.pending })}</span>
        )}
        {s.problems > 0 && (
          <button className="sync-pill problem" onClick={() => setOpen(true)}>
            ⚠ {t('offline.problems', { n: s.problems })}
          </button>
        )}
      </div>
      {open && <ProblemsDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function ProblemsDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const s = useOfflineState();
  const [items, setItems] = useState<SyncProblem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void listProblems().then(setItems);
  }, [s.problems]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dismiss = async (p: SyncProblem) => {
    await dismissProblem(p.id!);
    if (items.length <= 1) onClose();
  };

  const useMine = async (p: SyncProblem) => {
    if (!p.cid || !p.noteId) return;
    try {
      setError(null);
      // Bewusst ohne optimistische Sperre: Die eigene Fassung ersetzt die des anderen Geräts
      const res = await api.updateNote(p.cid, p.noteId, { body: p.local?.body, frontmatter: p.local?.frontmatter });
      qc.setQueryData(['note', p.cid, p.noteId], res);
      await dismiss(p);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal sync-problems" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('offline.problemsTitle')}>
        <h2>{t('offline.problemsTitle')}</h2>
        {error && <div className="error">{error}</div>}
        {items.length === 0 && <p className="muted">{t('offline.problemsEmpty')}</p>}
        <ul className="problem-list">
          {items.map((p) => (
            <li key={p.id} className={classNames('problem-item', p.kind)}>
              <div className="problem-head">
                <strong>{p.label}</strong>
                <span className="muted"> · {new Date(p.at).toLocaleString('de-DE')}</span>
              </div>
              <div>{p.message}</div>
              {p.kind === 'conflict' && p.local?.body !== undefined && <pre className="problem-local">{p.local.body}</pre>}
              <div className="problem-actions">
                {p.kind === 'conflict' ? (
                  <>
                    <button className="primary" onClick={() => void useMine(p)}>
                      {t('offline.useMine')}
                    </button>
                    {p.cid && p.noteId && (
                      <Link to={`/c/${p.cid}/n/${p.noteId}`} onClick={onClose}>
                        {t('offline.openNote')}
                      </Link>
                    )}
                    <button onClick={() => void dismiss(p)}>{t('offline.discard')}</button>
                  </>
                ) : (
                  <button onClick={() => void dismiss(p)}>{t('offline.dismiss')}</button>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="modal-actions">
          <button onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
}
